
import { ClampPoint, ClampTexture, ClampTextureHighLight, DYMODE_MODE, LineT, MOUSE, ModelUtils, STATUS_TYPE, TnEngineContext, TnEngineExtraContext, ViewEditor } from 'pytha';
import { Vector3, Points, Float32BufferAttribute, PointsMaterial, Object3D, BufferGeometry } from 'three';
import { CableT } from '../models/CableT';
import { UpdateCableCommand } from '../commands/UpdateCableCommand';
import { DEVICEBASE_SINGAL } from '../enums/devicebase-singal';
import { DEVICE_SUB_CONTROL } from '../enums/device-sub-controls';

export class CableMiddleClamp implements ClampPoint {
    uuid: string;
    entity: CableT;
    attachPoint: Vector3;
    linkPoints: Vector3[];
    editor: ViewEditor;
    viewObj: Points;
    key: string;
    location: string;

    _status: number = STATUS_TYPE.NONE;
    _entityClone: CableT;

    _activate = false;

    _size = 10;

    orthogonalPoint: Vector3;
    capturePoint: Vector3;
    polarPoint: Vector3;
    _helpLine: LineT;
    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    constructor(editor: ViewEditor, entity: CableT, point: Vector3, key: string, location: string) {
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();
        this.uuid = ModelUtils.generateUUID();
        this.editor = editor;
        this.entity = entity;
        this.attachPoint = point.clone();
        this.key = key;
        this.location = location;
    }

    get activate(): boolean {
        return this._activate;
    }
    set activate(value: boolean) {
        if (this._status != STATUS_TYPE.NONE) return;
        this._activate = value;
        if (!!value) {
            this.editor.renderer.domElement.addEventListener('pointerdown', this.onPointerdown);
        } else {
            this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerdown);
            this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        }
    }

    set(entity, point) {
        this.entity = entity;
        this.attachPoint = point;
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    _pointListClone: Vector3[] = [];
    point_down = new Vector3();
    onPointerdown = (event) => {

        if (event.button !== MOUSE.LEFT) return;
        event.stopPropagation();

        if (!!this.location) {
            // 激活编辑二级控制器
            this.editor.selectControl.resetSelectedEntities();
            this.unHighLight();
            this.editor.listeners.signals.onOpeCommandActive.dispatch(DEVICE_SUB_CONTROL.UPDATECABLEEND.name);
            this.editor.listeners.getSignal(DEVICEBASE_SINGAL.onUpadteCableEnd).dispatch(this.entity, this.location);
            return;
        }

        let rect = this.editor.renderer.domElement.getBoundingClientRect();
        let x = (event.clientX - rect.x) / rect.width * 2 - 1;
        let y = -(event.clientY - rect.y) / rect.height * 2 + 1;

        this.context.ucsContext.unprojectCursorPoint(x, y, this.editor.camera, this.point_down);

        if (this.capturePoint) {
            this.point_down.copy(this.capturePoint);
        } else if (this.orthogonalPoint) {
            this.point_down.copy(this.orthogonalPoint);
        } else if (this.polarPoint) {
            this.point_down.copy(this.polarPoint);
        }

        // 不投影到平面
        // let foot = this.point_down.clone();
        // this.entity._geoPlane.projectPoint(this.point_down, foot);
        // this.point_down.copy(foot);

        if (this._status === STATUS_TYPE.NONE) {
            this._entityClone = this.entity.clone();
            this._entityClone.uuid = this.entity.uuid;
            this._pointListClone = [];
            this._entityClone.pointList.forEach(point => {
                this._pointListClone.push(point.clone());
            })
            this.extraContext.sceneContext.scene.add(this._entityClone.render());
            //add helpline
            this._helpLine = new LineT(this.attachPoint, this.attachPoint);
            this._helpLine.color.set('#969c9a');
            this.extraContext.sceneContext.scene.add(this._helpLine.render());

            this.editor.selectControl.setEnabled(false);
            //capture start
            this.editor.listeners.signals.needCapture.dispatch();
            this.editor.listeners.signals.onPointCapture.add(this.onPointCapture);
            //orthogonal start
            this.editor.listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
            this.editor.listeners.signals.orthogonal.dispatch(this.attachPoint);
            // polarTracking start
            this.editor.listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
            this.editor.listeners.signals.polarTracking.dispatch(this.attachPoint);
            this._status = STATUS_TYPE.CLAMP;
            this.editor.listeners.signals.clampControlDisabled.dispatch();
            this.editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
            this.highLight();
            this.extraContext.dymodeContext.setMode(DYMODE_MODE.NONE);
            return;
        } else if (this._status === STATUS_TYPE.CLAMP) {
            this.point_delta.subVectors(this.point_down, this.attachPoint);
            this._entityClone.copy(this.entity);
            this.updateEntityPoints();
            this._entityClone.isSelected = this.entity.isSelected;
            this.editor.history.execute(new UpdateCableCommand(this.editor, this.entity, this._entityClone));
            this.extraContext.sceneContext.scene.remove(this._entityClone.render());
            this.extraContext.sceneContext.scene.remove(this._helpLine.render());

            this.editor.selectControl.setEnabled(true);
            this.editor.orthogonalControl.enabled = false;
            this.editor.polarControl.enabled = false;
            this.editor.listeners.signals.clampControlEnabled.dispatch();
            this.editor.listeners.signals.onClampNeedUpdate.dispatch(this.entity);

            this.editor.listeners.signals.noNeedCapture.dispatch();
            this.editor.listeners.signals.onPointCapture.remove(this.onPointCapture);
            this.editor.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
            this.editor.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
            this._status = STATUS_TYPE.NONE;
            this.editor.listeners.signals.needRender.dispatch();
            this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
            this.unHighLight();
            this.activate = false;
            this.extraContext.dymodeContext.setMode(DYMODE_MODE.COMMAND);
        }
    }

    point_move = new Vector3();
    point_delta = new Vector3();
    // private _foot = new Vector3();

    onPointerMove = (event) => {
        this.point_move.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        } else if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        } else if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }
        // 不投影到平面
        // this.entity._geoPlane.projectPoint(this.point_move, this._foot);
        // this.point_move.copy(this._foot);
        this.point_delta.subVectors(this.point_move, this.attachPoint);
        this._entityClone.copy(this.entity);
        this.updateEntityPoints();
        this._helpLine.setValue('end', this.point_move);
        this._helpLine.updateViewGeometry();
        this.editor.listeners.signals.needRender.dispatch();
    }

    updateEntityPoints = () => {
        let key = this.key;
        this._entityClone.selectedPoints = [];
        if (key[0] === "c") {
            let keys = key.split('-');
            let idx_last = keys[1];
            let idx = keys[2];
            this._entityClone.setValue(idx_last, this._pointListClone[+idx_last].clone().add(this.point_delta));
            this._entityClone.setValue(idx, this._pointListClone[+idx].clone().add(this.point_delta));
            this._entityClone.selectedPoints.push(this._pointListClone[+idx].clone());
            this._entityClone.selectedPoints.push(this._pointListClone[+idx].clone());
        } else {
            this._entityClone.setValue(key, this._pointListClone[+key].clone().add(this.point_delta));
            this._entityClone.selectedPoints.push(this._pointListClone[+key].clone());
        }
        this._entityClone.stretch(this.point_delta.clone());
        this._entityClone.updateViewGeometry();
    }

    updateGeometry = (value: Vector3) => {
        this.attachPoint.copy(value);
        let point = this.viewObj;
        if (!point) return;
        point.geometry.setAttribute('position', new Float32BufferAttribute([this.attachPoint.x, this.attachPoint.y, this.attachPoint.z], 3));
        point.geometry.computeBoundingSphere();
    }

    highLight = () => {
        let point = this.viewObj;
        if (!point) return;
        let mat = <PointsMaterial>point.material;
        mat.map = ClampTextureHighLight;
    }

    unHighLight = () => {
        let point = this.viewObj;
        if (!point) return;
        let mat = <PointsMaterial>point.material;
        mat.map = ClampTexture;
    }

    render(): Object3D {
        if (this.viewObj) return this.viewObj;

        const texture = ClampTexture;

        const geo = new BufferGeometry();
        geo.setAttribute('position', new Float32BufferAttribute([this.attachPoint.x, this.attachPoint.y, this.attachPoint.z], 3));

        const point = new Points(geo, new PointsMaterial({ map: texture, size: this._size }))

        this.viewObj = point;
        return point;
    }

    dispose() {
        this._status = STATUS_TYPE.NONE;
        this.activate = false;
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.editor.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.editor.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.editor.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        this.editor.selectControl.setEnabled(true);
        this.editor.orthogonalControl.enabled = false;
        this.editor.polarControl.enabled = false;
        if (this.viewObj && this.viewObj.parent) {
            this.viewObj.parent.remove(this.viewObj);
        }
        if (this._entityClone && this._entityClone.viewObj && this._entityClone.viewObj.parent) {
            this._entityClone.viewObj.parent?.remove(this._entityClone.viewObj);
        }
        if (this._helpLine && this._helpLine.viewObj && this._helpLine.viewObj.parent) {
            this._helpLine.viewObj.parent?.remove(this._helpLine.viewObj);
        }
        this.viewObj = null;
    }


}